import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, CircularProgress, FormControl, IconButton,
  InputAdornment, InputLabel, MenuItem, Paper, Select, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  AccountTreeOutlined, Add, CenterFocusStrong, ExpandLess, ExpandMore,
  Remove, Search,
} from '@mui/icons-material';

import { GET_ORGANIZATION_HIERARCHY } from '../../api';

const CARD_WIDTH = 238;

const matchesSearch = (node, query) => [
  node.full_name, node.employee_no, node.designation, node.department, node.branch,
].some((value) => String(value || '').toLowerCase().includes(query));

const filterTree = (nodes, rawQuery) => {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return nodes;
  return nodes.reduce((results, node) => {
    const children = filterTree(node.children || [], query);
    if (matchesSearch(node, query) || children.length) {
      results.push({ ...node, children });
    }
    return results;
  }, []);
};

function EmployeeNode({ node, collapsed, onToggle, searchActive }) {
  const children = node.children || [];
  const isCollapsed = !searchActive && collapsed.has(node.id);
  const managerNeedsReview = node.relationship_status === 'unresolved';
  const initials = node.full_name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  return (
    <Box component="li" sx={{ listStyle: 'none', position: 'relative', px: 1.25, textAlign: 'center' }}>
      <Paper
        data-testid={`employee-node-${node.employee_no}`}
        elevation={0}
        sx={{
          width: CARD_WIDTH,
          mx: 'auto',
          p: 2,
          border: '1px solid #e2e8f0',
          borderRadius: 3,
          bgcolor: '#fff',
          boxShadow: '0 10px 26px rgba(15, 23, 42, 0.08)',
          textAlign: 'left',
        }}
      >
        <Stack direction="row" spacing={1.4} alignItems="center">
          <Avatar src={node.avatar_url || undefined} alt={node.full_name} sx={{ bgcolor: '#315efb', width: 44, height: 44 }}>
            {initials}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography fontWeight={800} noWrap title={node.full_name}>{node.full_name}</Typography>
            <Typography variant="caption" color="text.secondary">{node.employee_no}</Typography>
          </Box>
        </Stack>
        <Stack spacing={0.45} sx={{ mt: 1.4 }}>
          <Typography variant="body2" fontWeight={700}>{node.designation || 'Designation not assigned'}</Typography>
          <Typography variant="caption" color="text.secondary">{node.department || 'Department not assigned'}</Typography>
          <Typography variant="caption" color="text.secondary">{node.branch || 'Branch not assigned'}</Typography>
        </Stack>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ mt: 1.4, flexWrap: 'wrap' }}>
          <Stack direction="row" gap={0.75} sx={{ flexWrap: 'wrap' }}>
            <Chip size="small" label={`${node.direct_reports_count} direct report${node.direct_reports_count === 1 ? '' : 's'}`} />
            {managerNeedsReview && <Chip size="small" color="warning" label="Manager match needed" />}
          </Stack>
          {children.length > 0 && (
            <Tooltip title={isCollapsed ? 'Expand direct reports' : 'Collapse direct reports'}>
              <IconButton
                size="small"
                aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} reports for ${node.full_name}`}
                onClick={() => onToggle(node.id)}
              >
                {isCollapsed ? <ExpandMore /> : <ExpandLess />}
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Paper>

      {children.length > 0 && !isCollapsed && (
        <Box sx={{ position: 'relative', pt: 4 }}>
          <Box sx={{ position: 'absolute', top: 0, left: '50%', width: 2, height: 32, bgcolor: '#cbd5e1' }} />
          <Box
            component="ul"
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              m: 0,
              p: 0,
              position: 'relative',
              '&::before': children.length > 1 ? {
                content: '""', position: 'absolute', top: 0,
                left: `${100 / (children.length * 2)}%`,
                right: `${100 / (children.length * 2)}%`,
                height: 2, bgcolor: '#cbd5e1',
              } : undefined,
            }}
          >
            {children.map((child) => (
              <Box
                key={child.id}
                sx={{ position: 'relative', pt: 4, '&::before': { content: '""', position: 'absolute', top: 0, left: '50%', width: 2, height: 32, bgcolor: '#cbd5e1' } }}
              >
                <EmployeeNode node={child} collapsed={collapsed} onToggle={onToggle} searchActive={searchActive} />
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default function OrganizationChart() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [showUnresolved, setShowUnresolved] = useState(false);
  const [selectedRootId, setSelectedRootId] = useState('');
  const [collapsed, setCollapsed] = useState(new Set());
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const drag = useRef(null);

  const loadHierarchy = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await GET_ORGANIZATION_HIERARCHY());
    } catch (requestError) {
      setData(null);
      setError(requestError?.response?.data?.detail || 'Unable to load the organization hierarchy.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadHierarchy(); }, [loadHierarchy]);

  const selectableRoots = useMemo(
    () => (data?.roots || []).filter((root) => root.relationship_status !== 'unresolved'),
    [data],
  );
  useEffect(() => {
    setSelectedRootId((current) => (
      selectableRoots.some((root) => String(root.id) === String(current))
        ? current
        : (selectableRoots[0]?.id || '')
    ));
  }, [selectableRoots]);
  const selectedRoot = useMemo(
    () => selectableRoots.find((root) => String(root.id) === String(selectedRootId)) || null,
    [selectableRoots, selectedRootId],
  );
  const visibleRoots = useMemo(() => {
    const roots = data?.roots || [];
    if (query.trim()) return filterTree(roots, query);
    if (showUnresolved) {
      return roots.filter((root) => root.relationship_status === 'unresolved');
    }
    return selectedRoot ? [selectedRoot] : [];
  }, [data, query, selectedRoot, showUnresolved]);
  const clampZoom = (value) => Math.min(1.6, Math.max(0.55, value));
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  const toggleNode = (id) => setCollapsed((current) => {
    const next = new Set(current);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const beginPan = (event) => {
    if (event.target.closest('button, input')) return;
    drag.current = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, origin: pan };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const movePan = (event) => {
    if (!drag.current || drag.current.pointerId !== event.pointerId) return;
    setPan({
      x: drag.current.origin.x + event.clientX - drag.current.x,
      y: drag.current.origin.y + event.clientY - drag.current.y,
    });
  };
  const endPan = () => { drag.current = null; };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#f5f7fb', minHeight: '100%' }}>
      <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, border: '1px solid #edf0f5' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
          <Box>
            <Stack direction="row" spacing={1.2} alignItems="center">
              <AccountTreeOutlined color="primary" />
              <Typography variant="h5" fontWeight={900}>Organization Chart</Typography>
            </Stack>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              {data?.company?.name || 'Your company'} reporting hierarchy
            </Typography>
          </Box>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search people, department, designation…"
            size="small"
            sx={{ width: { xs: '100%', md: 360 } }}
            inputProps={{ 'aria-label': 'Search organization chart' }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }}
          />
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap' }}>
          <Chip label={`${data?.employee_count || 0} active employees`} color="primary" variant="outlined" />
          {(data?.unresolved_relationship_count || 0) > 0 && (
            <Button
              size="small"
              color="warning"
              variant={showUnresolved ? 'contained' : 'outlined'}
              onClick={() => setShowUnresolved((current) => !current)}
            >
              {showUnresolved ? 'Back to hierarchy' : `Review ${data.unresolved_relationship_count} unmatched managers`}
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Zoom out"><span><IconButton aria-label="Zoom out" disabled={loading} onClick={() => setZoom((value) => clampZoom(value - 0.1))}><Remove /></IconButton></span></Tooltip>
          <Typography variant="body2" minWidth={48} textAlign="center">{Math.round(zoom * 100)}%</Typography>
          <Tooltip title="Zoom in"><span><IconButton aria-label="Zoom in" disabled={loading} onClick={() => setZoom((value) => clampZoom(value + 0.1))}><Add /></IconButton></span></Tooltip>
          <Button startIcon={<CenterFocusStrong />} onClick={resetView} disabled={loading}>Reset view</Button>
        </Stack>

        {!loading && !error && !showUnresolved && !query.trim() && selectableRoots.length > 0 && (
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ mt: 2, p: 2, borderRadius: 3, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}
          >
            <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 340 } }}>
              <InputLabel id="hierarchy-head-label">Hierarchy head</InputLabel>
              <Select
                labelId="hierarchy-head-label"
                label="Hierarchy head"
                value={selectedRootId}
                onChange={(event) => {
                  setSelectedRootId(event.target.value);
                  resetView();
                }}
              >
                {selectableRoots.map((root) => (
                  <MenuItem key={root.id} value={root.id}>
                    {root.full_name} ({root.employee_no}) — {root.total_reports_count || 0} reports
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {selectedRoot && (
              <Box>
                <Typography fontWeight={900}>{selectedRoot.full_name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Primary view · {selectedRoot.direct_reports_count} direct reports · {selectedRoot.total_reports_count || 0} total reports
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </Paper>

      <Paper
        elevation={0}
        data-testid="organization-chart-viewport"
        onPointerDown={beginPan}
        onPointerMove={movePan}
        onPointerUp={endPan}
        onPointerCancel={endPan}
        sx={{
          mt: 2, minHeight: { xs: 480, md: 620 }, height: 'calc(100vh - 285px)',
          overflow: 'hidden', borderRadius: 4, border: '1px solid #edf0f5',
          cursor: drag.current ? 'grabbing' : 'grab', position: 'relative', touchAction: 'none',
        }}
      >
        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }} spacing={1.5}>
            <CircularProgress aria-label="Loading organization hierarchy" />
            <Typography color="text.secondary">Loading organization hierarchy…</Typography>
          </Stack>
        )}

        {!loading && error && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 3 }}>
            <Alert severity="error" action={<Button color="inherit" onClick={loadHierarchy}>Retry</Button>}>
              {error}
            </Alert>
          </Stack>
        )}

        {!loading && !error && data?.employee_count === 0 && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 3 }} spacing={1}>
            <AccountTreeOutlined sx={{ fontSize: 54, color: '#94a3b8' }} />
            <Typography variant="h6" fontWeight={800}>No active employees found</Typography>
            <Typography color="text.secondary">The hierarchy will appear after active employees and reporting managers are assigned.</Typography>
          </Stack>
        )}

        {!loading && !error && data?.employee_count > 0 && visibleRoots.length === 0 && (
          <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 3 }} spacing={1}>
            <Search sx={{ fontSize: 48, color: '#94a3b8' }} />
            <Typography variant="h6" fontWeight={800}>No matching employees</Typography>
            <Typography color="text.secondary">Try a different name, employee ID, department, designation, or branch.</Typography>
          </Stack>
        )}

        {!loading && !error && visibleRoots.length > 0 && !showUnresolved && (
          <Box
            data-testid="organization-chart-canvas"
            sx={{
              minWidth: 'max-content', p: 5, transformOrigin: 'top left',
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
              transition: drag.current ? 'none' : 'transform 120ms ease-out',
            }}
          >
            <Box component="ul" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: 5, m: 0, p: 0 }}>
              {visibleRoots.map((root) => (
                <EmployeeNode key={root.id} node={root} collapsed={collapsed} onToggle={toggleNode} searchActive={Boolean(query.trim())} />
              ))}
            </Box>
          </Box>
        )}

        {!loading && !error && visibleRoots.length > 0 && showUnresolved && (
          <Box sx={{ height: '100%', overflow: 'auto', p: 3 }}>
            <Typography variant="h6" fontWeight={900}>Reporting manager review</Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              These employees contain a manager value that cannot be matched safely. Select the correct manager in Employee Management; no relationship is guessed here.
            </Typography>
            <Box
              component="ul"
              sx={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))',
                gap: 2, m: 0, p: 0, alignItems: 'start',
              }}
            >
              {visibleRoots.map((root) => (
                <EmployeeNode key={root.id} node={root} collapsed={collapsed} onToggle={toggleNode} searchActive={false} />
              ))}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
}
